import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getFormUrl } from '@/utils/form';

export default function Admissions() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [phoneAlt, setPhoneAlt] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  const faqs = [
    { q: t('admissions.faq.q1'), a: t('admissions.faq.a1') },
    { q: t('admissions.faq.q2'), a: t('admissions.faq.a2') },
    { q: t('admissions.faq.q3'), a: t('admissions.faq.a3') },
    { q: t('admissions.faq.q5'), a: t('admissions.faq.a5') },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (phoneAlt.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setPhoneAlt('');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    const formUrl = getFormUrl('solicitarInformacion');
    if (!formUrl) {
      setFormStatus('error');
      setFormError(t('admissions.form.error'));
      return;
    }

    const now = new Date();
    const dateTimeStr = now.toLocaleString('es-CR', { timeZone: 'America/Costa_Rica' });

    const rawData = new FormData(e.currentTarget);
    const body = new URLSearchParams();
    body.append('name', (rawData.get('name') as string) || formData.name);
    body.append('email', (rawData.get('email') as string) || formData.email);
    body.append('phone', (rawData.get('phone') as string) || formData.phone);
    body.append('subject', (rawData.get('subject') as string) || formData.subject);
    body.append('message', (rawData.get('message') as string) || formData.message);
    body.append('fecha_envio', dateTimeStr);
    body.append('_subject', 'San Lorenzo School - Solicitud de información');
    body.append('_template', 'table');

    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { success?: string | boolean; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* non-JSON response */ }

      const serverMsg = parsed?.message || '';
      const submitSucceeded = response.ok && (parsed?.success === 'true' || parsed?.success === true || !responseText);

      if (submitSucceeded) {
        setFormStatus('success');
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setPhoneAlt('');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || t('admissions.form.error'));
      }
    } catch {
      setFormStatus('error');
      setFormError(t('admissions.form.error'));
    }
  };

  const steps = [
    { icon: 'ri-file-list-line', title: t('admissions.process.step1'), desc: t('admissions.process.step1Desc') },
    { icon: 'ri-walk-line', title: t('admissions.process.step2'), desc: t('admissions.process.step2Desc') },
    { icon: 'ri-user-voice-line', title: t('admissions.process.step3'), desc: t('admissions.process.step3Desc') },
    { icon: 'ri-check-double-line', title: t('admissions.process.step4'), desc: t('admissions.process.step4Desc') },
  ];

  const requirements = [
    t('admissions.requirements.item1'),
    t('admissions.requirements.item2'),
    t('admissions.requirements.item3'),
    t('admissions.requirements.item4'),
    t('admissions.requirements.item5'),
    t('admissions.requirements.item6'),
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[400px] md:h-[560px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/007f4259-8878-46d0-9fbc-7e2b9f0ad119_compressed_AdmisionesF_comprimida.webp"
            alt="Admisiones"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-purple-900/60 via-purple-900/50 to-purple-900/70" />
        </div>
        <div className="relative z-10 text-center px-4">
          <span className="text-yellow-400 text-xs font-semibold tracking-[0.2em] uppercase">{t('admissions.heroBadge')}</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-white mt-4 leading-tight">{t('admissions.title')}</h1>
          <p className="text-white/70 mt-4 max-w-2xl mx-auto text-base leading-relaxed">{t('admissions.subtitle')}</p>
        </div>
      </section>

      {/* Process Steps */}
      <section id="process" data-animate className="py-20 md:py-28 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="text-center mb-16 md:mb-20">
            <span className="text-yellow-500 text-xs font-semibold tracking-[0.2em] uppercase">{t('admissions.process.badge')}</span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-purple-900 mt-4 leading-tight">{t('admissions.process.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-6">
            {steps.map((step, index) => (
              <div
                key={index}
                className={`relative text-center transition-all duration-700 ${
                  isVisible['process'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-teal-500 flex items-center justify-center">
                  <i className={`${step.icon} text-yellow-400 text-2xl`} />
                </div>
                <div className="w-8 h-8 mx-auto mb-4 rounded-full bg-yellow-500 flex items-center justify-center">
                  <span className="text-white font-serif text-sm font-bold">{index + 1}</span>
                </div>
                <h3 className="font-serif text-lg font-semibold text-purple-900 mb-3">{step.title}</h3>
                <p className="text-black/50 text-sm leading-relaxed">{step.desc}</p>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-14 left-[calc(50%+50px)] w-[calc(100%-100px)] h-px bg-purple-200/60" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section id="requirements" data-animate className="py-20 md:py-28 bg-purple-50">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            <div className={`rounded-2xl overflow-hidden flex items-center justify-center transition-all duration-700 ${isVisible['requirements'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <img
                src="https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/66598d65-5108-4541-92f7-45ace61d7bc7_compressed_Bienvenida.webp"
                alt={t('admissions.requirements.title')}
                className="w-full h-full object-cover"
              />
            </div>
            <div className={`transition-all duration-700 delay-150 ${isVisible['requirements'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <span className="text-yellow-500 text-xs font-semibold tracking-[0.2em] uppercase">{t('admissions.requirements.badge')}</span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-purple-900 mt-4 mb-8 leading-tight">{t('admissions.requirements.title')}</h2>
              <ul className="space-y-4">
                {requirements.map((req, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-yellow-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-yellow-500 text-sm" />
                    </div>
                    <span className="text-black/60 text-sm leading-relaxed">{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Solicitar Información Form */}
      <section id="info-form" className="py-20 md:py-28 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-yellow-500 text-xs font-semibold tracking-[0.2em] uppercase">{t('admissions.form.badge')}</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-purple-900 mt-4 leading-tight">{t('admissions.form.title')}</h2>
            <p className="text-black/50 mt-4 max-w-xl mx-auto text-sm leading-relaxed">{t('admissions.form.subtitle')}</p>
          </div>

          <div className="max-w-2xl mx-auto">
            <form id="solicitar-informacion" data-readdy-form onSubmit={handleSubmit} className="bg-purple-50 rounded-2xl p-6 md:p-10 border border-purple-100 space-y-4">
              <div>
                <label className="block text-sm font-medium text-black/70 mb-1.5">{t('admissions.form.name')}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black/70 mb-1.5">{t('admissions.form.email')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black/70 mb-1.5">{t('admissions.form.phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-black/70 mb-1.5">{t('admissions.form.subject')}</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white transition-colors appearance-none"
                >
                  <option value="">{t('admissions.form.subjectPlaceholder')}</option>
                  <option value="Preescolar">{t('admissions.form.preschool')}</option>
                  <option value="Primaria">{t('admissions.form.primary')}</option>
                  <option value="Secundaria">{t('admissions.form.secondary')}</option>
                  <option value="Información General">{t('admissions.form.generalInfo')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-black/70 mb-1.5">{t('admissions.form.message')}</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-xl border border-purple-200 focus:border-purple-800 focus:ring-1 focus:ring-purple-800 outline-none text-sm bg-white resize-none transition-colors"
                />
                <p className="text-xs text-black/30 mt-1 text-right">{formData.message.length}/500</p>
              </div>
              <input
                type="text"
                name="phone_alt"
                value={phoneAlt}
                onChange={(e) => setPhoneAlt(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="visually-hidden-field"
              />
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full px-6 py-3.5 bg-purple-800 text-white font-semibold rounded-xl hover:bg-purple-900 hover:shadow-lg hover:shadow-purple-800/20 transition-all duration-300 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {formStatus === 'loading' ? t('admissions.form.sending') : t('admissions.form.submit')}
              </button>
              {formStatus === 'success' && (
                <p className="text-teal-600 text-sm text-center bg-teal-50 py-3 rounded-xl">{t('admissions.form.success')}</p>
              )}
              {formStatus === 'error' && (
                <p className="text-yellow-600 text-sm text-center bg-yellow-500/10 py-3 rounded-xl">{formError || t('admissions.form.error')}</p>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-animate className="py-20 md:py-28 bg-purple-50">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="text-center mb-14 md:mb-16">
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-purple-900 leading-tight">
              {t('admissions.faq.title')}
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl border border-purple-100 overflow-hidden transition-all duration-700 ${
                  isVisible['faq'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${index * 80}ms` }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-purple-50/60 transition-colors"
                  aria-expanded={openFaq === index}
                >
                  <span className="font-semibold text-purple-900 text-sm pr-4 leading-snug">{faq.q}</span>
                  <i
                    className={`ri-arrow-down-s-line text-purple-800 text-xl flex-shrink-0 transition-transform duration-300 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-black/50 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-purple-800">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            {t('admissions.cta.title')}
          </h2>
          <p className="text-white/60 text-lg mb-10 font-light">
            {t('admissions.cta.subtitle')}
          </p>
          <a
            href={`https://wa.me/50683495115?text=${encodeURIComponent(t('admissions.whatsappMessage'))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 transition-all duration-300 text-sm tracking-wide"
          >
            <i className="ri-whatsapp-line text-xl" />
            {t('admissions.cta.button')}
          </a>
        </div>
      </section>
    </div>
  );
}